import { BadgeInfoComponent } from '@components/shared/badge-info/badge-info.component';
import { ChainMarksComponent } from '@components/shared/chain-marks/chain-marks.component';
// ErrorStateComponent removed as it's not used in the template
import { EmptyStateComponent } from '@components/shared/empty-state/empty-state.component';
import { FlatInfoComponent } from '@components/shared/flat-info/flat-info.component';
import { PaginationComponent } from '@components/shared/pagination/pagination.component';
import { PaginationEvent } from '@models/pagination.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LogService } from '@services/util/log.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { PaginatorModule } from 'primeng/paginator';
import { ProgressBarModule } from 'primeng/progressbar';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, finalize, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BookingFilter } from '../../models/booking-filter.model';
import { BookingRow, PaginatedBooking } from '../../models/booking.model';
import { BookingService } from '../../services/booking.service';

@Component({
  selector: 'app-bookings-table',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    PaginatorModule,
    SkeletonModule,
    ProgressBarModule,
    TooltipModule,
    ConfirmDialogModule,
    ToastModule,
    EmptyStateComponent,
    PaginationComponent,
    FlatInfoComponent,
    BadgeInfoComponent,
    ChainMarksComponent
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './bookings-table.component.html'
})
export class BookingsTableComponent implements OnInit, OnDestroy {
  // Default page size - matches API default of 30 (the API ignores the pageSize param,
  // so this must mirror the server-side default for the paginator math to be correct).
  pageSize = 30;

  // Current page (1-based)
  currentPage = 1;

  // Bookings data with pagination details
  paginatedBookings: PaginatedBooking | null = null;

  // Table data
  bookings: BookingRow[] = [];
  totalRecords = 0;

  // Loading state
  loading = false;

  // Current sort field and order - for API calls
  sortField: string | null = 'start_date';
  sortOrder: 'asc' | 'desc' | null = 'desc';

  // Keeps track of which column is sorted and in what direction - for UI display
  sortState: { field: string | null; direction: 'asc' | 'desc' | null } = {
    field: 'checkInDate',
    direction: 'desc'
  };

  // Mapping between UI field names and API field names
  private readonly fieldMapping: Record<string, string | null> = {
    startDate: 'start_date',
    endDate: 'end_date',
    guestName: 'name',
    guestCount: 'adult_occupants',
    status: 'bookingStatus.id'
  };

  // Active filters object
  activeFilter?: BookingFilter;

  // Error state
  private readonly errorSubject = new BehaviorSubject<string | null>(null);
  readonly error$ = this.errorSubject.asObservable();

  private initialLoadCompleted = false;

  // Input for external filter changes
  @Input() set filter(filter: BookingFilter | undefined) {
    if (filter !== undefined) {
      this.activeFilter = Object.keys(filter).length > 0 ? filter : undefined;
      if (this.initialLoadCompleted) {
        this.currentPage = 1;
        this.updateUrlParams();
        this.loadBookings();
      }
    } else if (this.initialLoadCompleted && this.activeFilter !== undefined) {
      this.activeFilter = undefined;
      this.currentPage = 1;
      this.updateUrlParams();
      this.loadBookings();
    }
  }

  // Output events
  @Output() viewBookingDetails = new EventEmitter<string>();

  // Subject for cleanup
  private destroy$ = new Subject<void>();

  constructor(
    private readonly confirmationService: ConfirmationService,
    private readonly translateService: TranslateService,
    private readonly messageService: MessageService,
    private readonly bookingService: BookingService,
    private readonly log: LogService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.restoreStateFromUrl();
    this.loadBookings();
    this.initialLoadCompleted = true;
  }

  private restoreStateFromUrl(): void {
    const params = this.route.snapshot.queryParams;
    if (params['page']) {
      const parsed = parseInt(params['page'], 10);
      if (!Number.isNaN(parsed) && parsed >= 1) {
        this.currentPage = parsed;
      }
    }
    if (params['sortField']) {
      const apiField = params['sortField'];
      const uiField = this.mapApiFieldToUi(apiField);
      if (uiField) {
        this.sortField = apiField;
        this.sortState.field = uiField;
      }
    }
    if (params['sortOrder'] === 'asc' || params['sortOrder'] === 'desc') {
      this.sortOrder = params['sortOrder'];
      this.sortState.direction = params['sortOrder'];
    }
  }

  private updateUrlParams(): void {
    const queryParams: Record<string, string | null> = {
      page: this.currentPage > 1 ? String(this.currentPage) : null,
      sortField: this.sortField ?? null,
      sortOrder: this.sortOrder ?? null
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private mapApiFieldToUi(apiField: string): string | null {
    for (const [uiKey, apiValue] of Object.entries(this.fieldMapping)) {
      if (apiValue === apiField) return uiKey;
    }
    return null;
  }

  /**
   * Custom sort handler for p-table
   * @param event Sort event containing field name and order
   */
  onSort(event: any): BookingRow[] {
    const clickedField = event.field;
    this.log.debug('Sort clicked:', clickedField, 'current state:', this.sortState);

    // If clicking on same column, toggle sort direction: none -> asc -> desc -> none
    if (clickedField === this.sortState.field) {
      // Toggle direction: null -> asc -> desc -> null
      if (this.sortState.direction === null) {
        this.sortState.direction = 'asc';
      } else if (this.sortState.direction === 'asc') {
        this.sortState.direction = 'desc';
      } else {
        // Reset sort completely when going beyond desc
        this.sortState.field = null;
        this.sortState.direction = null;
      }
    } else {
      // Clicking new column, start with asc
      this.sortState.field = clickedField;
      this.sortState.direction = 'asc';
    }

    // Map UI field to API field for sorting
    this.sortField = this.mapSortFieldToApi(this.sortState.field);
    this.sortOrder = this.sortState.direction;

    this.log.debug('Mapped sort field', {
      uiField: this.sortState.field,
      apiField: this.sortField,
      direction: this.sortOrder
    });

    // Reload data with new sort - don't use PrimeNG's local sort
    this.updateUrlParams();
    this.loadBookings();

    // Return unchanged data since we're not using local sort
    return event.data || [];
  }

  /**
   * Handle pagination event from shared pagination component
   * @param event Pagination event containing page number
   */
  onPaginationChange(event: PaginationEvent): void {
    this.currentPage = event.page;
    this.pageSize = event.pageSize;
    this.updateUrlParams();
    this.loadBookings();
  }

  /**
   * Show confirmation dialog before deleting a booking
   * @param id Booking ID to delete
   * @param event Click event
   */
  confirmDelete(id: string, event: Event): void {
    event.stopPropagation();

    this.confirmationService.confirm({
      message: this.translateService.instant('bookings.confirmDelete'),
      header: this.translateService.instant('common.confirm'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.onDeleteBooking(id);
      }
    });
  }

  /**
   * Navigate to booking details page
   * @param id Booking ID to view
   */
  onRowClick(id: string): void {
    this.viewBookingDetails.emit(id);
  }

  /**
   * Check if the booking status is active/upcoming
   * @param status Booking status
   * @returns Whether the booking is active
   */
  isActiveBooking(status: string): boolean {
    return ['confirmed', 'pending', 'active'].includes(status.toLowerCase());
  }

  /**
   * Get the CSS class for the booking status badge
   * @param status Booking status
   * @returns CSS class for the status badge
   */
  getStatusClass(status: string): string {
    const statusLower = status.toLowerCase();

    if (statusLower.includes('cancel')) {
      return 'bg-red-100 text-red-800';
    }

    if (statusLower.includes('confirm')) {
      return 'bg-green-100 text-green-800';
    }

    if (statusLower.includes('pend')) {
      return 'bg-yellow-100 text-yellow-800';
    }

    if (statusLower.includes('complet')) {
      return 'bg-blue-100 text-blue-800';
    }

    return 'bg-gray-100 text-gray-800';
  }

  /**
   * Cleanup resources on component destruction
   */
  /**
   * Handle filter changes
   */
  onFilterChange(filter: BookingFilter): void {
    // Save active filter
    this.activeFilter = Object.keys(filter).length > 0 ? filter : undefined;
    this.log.debug('Applied filters:', this.activeFilter);

    // Reset to first page when applying filters
    this.currentPage = 1;

    // Load with filters
    this.loadBookings();
  }

  /**
   * Reset filters
   */
  onResetFilters(): void {
    this.activeFilter = undefined;
    this.currentPage = 1;
    this.loadBookings();
  }

  /**
   * Maps UI field names to API field names for sorting
   * @param uiField Field name used in the UI
   * @returns API field name or null if not sortable
   */
  private mapSortFieldToApi(uiField: string | null): string | null {
    if (!uiField) return null;
    return this.fieldMapping[uiField] ?? null;
  }

  /**
   * Load bookings with current pagination, sorting and filtering
   */
  loadBookings(): void {
    // Set loading state before making any API call
    this.loading = true;
    this.errorSubject.next(null);

    this.log.debug('Loading bookings with:', {
      page: this.currentPage,
      pageSize: this.pageSize,
      sortField: this.sortField, // Already mapped to API field
      sortOrder: this.sortOrder,
      filters: this.activeFilter
    });

    // Use mapped sort field
    const orderField = this.sortField;
    const orderDirection = this.sortOrder;

    this.bookingService
      .getBookings(this.currentPage, this.pageSize, orderField, orderDirection, this.activeFilter)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          // Always set loading to false in finalize to ensure it happens even in error cases
          this.loading = false;
        })
      )
      .subscribe({
        next: (result: PaginatedBooking) => {
          // Update all data properties in one handler to avoid change detection issues
          this.paginatedBookings = result;
          this.bookings = result.items;
          this.totalRecords = result.totalCount;
          this.log.debug('Loaded bookings:', result);
        },
        error: (err) => {
          this.errorSubject.next('Failed to load bookings');
          this.log.error('Error loading bookings:', err);
        }
      });
  }

  /**
   * Delete a booking
   * @param id Booking ID to delete
   */
  onDeleteBooking(id: string): void {
    this.loading = true;

    this.bookingService
      .deleteBooking(id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.success'),
              detail: this.translateService.instant('bookings.deleteSuccess')
            });
            this.loadBookings();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.error'),
              detail: this.translateService.instant('bookings.deleteError')
            });
          }
        },
        error: (err) => {
          this.log.error('Error deleting booking', err);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.error'),
            detail: this.translateService.instant('bookings.deleteError')
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
